/**
 * Main Application Component
 * 
 * This is the root component of the parking management system application.
 * It handles global application initialization, theme management, timezone settings,
 * and session management across the entire application.
 * 
 * Key Features:
 * - Dark/Light theme switching with automatic body class management
 * - Global timezone configuration and change handling
 * - Session initialization and monitoring
 * - Global notification and warning system integration
 * 
 * @author Parking System Team
 * @version 1.0.0
 */

import { Component, effect, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TuiRoot } from '@taiga-ui/core';
import { UiThemeService } from './shared/service/ui-theme';
import { LayoutWrapper } from './layout-wrapper/layout-wrapper';
import { GlobalTimezoneService } from './shared/services/global-timezone.service';
import { GlobalNotificationComponent } from './shared/components/global-notification/global-notification';
// Session warning component disabled - popup removed as per requirement
// import { SessionWarningComponent } from './shared/components/session-warning/session-warning.component';
import { SessionInitializationService } from './shared/services/session-initialization.service';
import { GlobalCurrencyManagerService } from './shared/services/global-currency-manager.service';
import { NetworkStatusService } from './shared/services/network-status.service';


@Component({
  selector: 'app-root',
  standalone: true,
    imports: [TuiRoot, LayoutWrapper, GlobalNotificationComponent], // SessionWarningComponent removed
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
})
export class App implements OnInit {
  // Dependency injection for core services
  private readonly _uiThemeService = inject(UiThemeService);
  private readonly _globalTimezoneService = inject(GlobalTimezoneService);
  private readonly _sessionInitializationService = inject(SessionInitializationService);
  private readonly _globalCurrencyManagerService = inject(GlobalCurrencyManagerService);
  private readonly _router = inject(Router);
  private readonly _networkStatusService = inject(NetworkStatusService);

  /**
   * Getter for current dark mode state
   * @returns boolean indicating if dark mode is currently active
   */
  get darkMode(): boolean {
    return this._uiThemeService.darkMode();
  }

  /**
   * Constructor - Sets up reactive theme management
   * Uses Angular's effect() to automatically toggle dark mode class on document body
   * when theme changes occur
   */
  constructor() {
    effect(() => {
      document.body.classList.toggle('dark', this.darkMode);
    });
  }

  /**
   * Component initialization lifecycle hook
   * Called after component construction and dependency injection
   */
  ngOnInit(): void {
    // Initialize network status monitoring
    this._networkStatusService.initialize(this._router);
    // Initialize global timezone handling
    this.initializeGlobalTimezone();
    // Sync global currency once at app start
    this._globalCurrencyManagerService.syncCurrencyFromOrganization();
  }

  /**
   * Initialize global timezone configuration and change handling
   * 
   * This method sets up:
   * - Automatic timezone service initialization
   * - Global timezone change event listeners
   * - Analytics and logging for timezone changes
   * 
   * @private
   */
  private initializeGlobalTimezone(): void {
    // The GlobalTimezoneService is already injected and will initialize automatically
    // This ensures the service is properly initialized when the app starts

    // Set up global timezone change listener for any components that need it
    this._globalTimezoneService.onTimezoneChange((event: CustomEvent) => {

      // You can add any global timezone change handling here
      // For example, updating analytics, logging, etc.
    });
  }
}
